/**
 * NavigationGuardContext
 *
 * Provides a way for pages to register a "should leave?" check that the
 * Layout's navigation handler will call before actually navigating. If the
 * check returns false, navigation is cancelled. This enables unsaved-changes
 * dialogs without requiring createBrowserRouter/useBlocker.
 */
import React, { createContext, useContext, useRef, useCallback } from 'react';

type GuardFn = (targetPath: string) => Promise<boolean> | boolean;

interface NavigationGuardContextValue {
  /** Register a guard function. Returns an unregister callback. */
  registerGuard: (fn: GuardFn) => () => void;
  /** Check all registered guards. Returns true if navigation is allowed. */
  checkGuards: (targetPath: string) => Promise<boolean>;
}

const NavigationGuardContext = createContext<NavigationGuardContextValue>({
  registerGuard: () => () => {},
  checkGuards: async () => true,
});

export const useNavigationGuard = () => useContext(NavigationGuardContext);

export const NavigationGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const guardsRef = useRef<Set<GuardFn>>(new Set());

  const registerGuard = useCallback((fn: GuardFn) => {
    guardsRef.current.add(fn);
    return () => { guardsRef.current.delete(fn); };
  }, []);

  const checkGuards = useCallback(async (targetPath: string) => {
    for (const guard of guardsRef.current) {
      const allowed = await guard(targetPath);
      if (!allowed) return false;
    }
    return true;
  }, []);

  return (
    <NavigationGuardContext.Provider value={{ registerGuard, checkGuards }}>
      {children}
    </NavigationGuardContext.Provider>
  );
};
